import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, fmtINR, imgUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Stethoscope, FolderTree, TrendingUp, Crown, Clock, CalendarCheck, UserPen, ArrowUpRight
} from "lucide-react";

const STAT_CONF = [
  { key: "total_procedures", label: "Total Procedures", icon: Stethoscope, color: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-950/40" },
  { key: "total_categories", label: "Total Categories", icon: FolderTree, color: "text-teal-600", bg: "bg-teal-50 dark:bg-teal-950/40" },
  { key: "average_pricing", label: "Average Pricing", icon: TrendingUp, color: "text-violet-600", bg: "bg-violet-50 dark:bg-violet-950/40", money: true },
  { key: "highest_price", label: "Highest Price", icon: Crown, color: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-950/40", money: true, sub: "highest_name" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)); }, []);

  if (!stats) return <div className="animate-pulse text-slate-400">Loading dashboard…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Dashboard</h2>
        <p className="text-slate-500 mt-1">Overview of your clinic's procedure pricing and activity.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {STAT_CONF.map((c, i) => (
          <motion.div key={c.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <Card className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200 h-full" data-testid={`stat-${c.key}`}>
              <div className={`h-11 w-11 rounded-xl ${c.bg} flex items-center justify-center mb-4`}>
                <c.icon className={`h-5 w-5 ${c.color}`} strokeWidth={1.9} />
              </div>
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-400">{c.label}</div>
              <div className="font-heading text-2xl font-extrabold mt-1">
                {c.money ? fmtINR(stats[c.key]) : stats[c.key]}
              </div>
              {c.sub && <div className="text-sm text-slate-500 mt-0.5 truncate">{stats[c.sub]}</div>}
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <Card className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center"><CalendarCheck className="h-5 w-5 text-emerald-600" /></div>
            <div>
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-400">Today's Updates</div>
              <div className="font-heading text-2xl font-extrabold">{stats.todays_updates}</div>
            </div>
          </div>
        </Card>
        <Card className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm lg:col-span-2">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-sky-50 dark:bg-sky-950/40 flex items-center justify-center"><UserPen className="h-5 w-5 text-sky-600" /></div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-400">Latest Modified By</div>
              <div className="font-heading text-xl font-bold truncate">{stats.latest_modified_by}</div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="rounded-xl border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2 font-heading font-bold"><Clock className="h-4 w-4 text-slate-400" /> Recently Updated Procedures</div>
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {stats.recent.map((p) => (
            <button key={p.id} onClick={() => navigate(`/procedures/${p.id}`)} data-testid={`recent-${p.id}`}
              className="w-full flex items-center gap-4 px-6 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors duration-150 text-left">
              <img src={imgUrl(p.cover_image)} alt={p.name} className="h-11 w-11 rounded-lg object-cover bg-slate-100" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{p.name}</div>
                <div className="text-sm text-slate-400">{p.category}</div>
              </div>
              <div className="text-right hidden sm:block">
                <div className="font-semibold">{fmtINR(p.avg_price)}</div>
                <div className="text-xs text-slate-400">avg price</div>
              </div>
              <ArrowUpRight className="h-4 w-4 text-slate-300" />
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
